import React from 'react';
import { css, CSSProperties, StyleSheet } from 'aphrodite/no-important';
import { VrLineHeight } from '../../../../../theming/verticalRhythm/verticalRhythm';
import { useTheme } from '../../../../../theming/useTheme';
import { PrimaryLinks } from './PrimaryLinks';
import { Logo, LogoProps } from '../Logo';
import { Editions, EditionDataProps } from './Editions';
import { CtaButton, CtaDataProps } from './CtaButton';
import { HeaderLinksDataProps } from '../Header';
import { Menu } from './menu/Menu';
import { primaryHeaderHeight } from '../header.utils';
import { MenuLinkDataProps } from './menu/links/MenuLink';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { VideoLogo, InjectedVideoLogoApiProps } from './VideoLogo';

const componentConfigThemeKey = 'primaryHeader';

export interface PrimaryHeaderDataProps {
  primaryLinks: HeaderLinksDataProps[] | null
  primaryLogos: LogoProps
  editions: EditionDataProps[] | null
  cta: CtaDataProps | null
  menu: MenuLinkDataProps[] | null
  videoLogo: InjectedVideoLogoApiProps | null
  pagePath: string,
  editionEndpoint: string
}

export interface PrimaryHeaderThemeProps {
  vrLineHeight: VrLineHeight
  backgroundColor: string
  surfaceStyle: CSSProperties
}

const getStyle = (vrLineHeight: VrLineHeight, backgroundColor: string) => {
  return StyleSheet.create({
    header: {
      backgroundColor,
      position: 'relative',
      height: `${primaryHeaderHeight}px`,
      display: 'flex',
      justifyContent: 'space-between',
      zIndex: 100,
    },
    itemsBasicContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      width: 'fit-content',
      alignItems: 'center',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      },
    },
    itemsRightContainer: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
      },
    },
    itemsLeftContainer: {
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      },
    },
  });
};

type propsType = PrimaryHeaderThemeProps & PrimaryHeaderDataProps;

export const PrimaryHeaderComponent: React.FunctionComponent<propsType> = props => {
  const { vrLineHeight, backgroundColor, primaryLinks, primaryLogos, editions, surfaceStyle, cta, menu, videoLogo, pagePath, editionEndpoint } = props;
  const style = getStyle(vrLineHeight, backgroundColor);
  return (
    <div className={css(style.header, StyleSheet.create({ surfaceStyle }).surfaceStyle)}>
      <div className={css(style.itemsBasicContainer, style.itemsLeftContainer)}>
        {menu ? <Menu menu={menu} editions={editions} editionEndpoint={editionEndpoint} /> : null}
        {videoLogo ? <VideoLogo {...videoLogo} href={primaryLogos.href} /> : <Logo {...primaryLogos} />}
        {primaryLinks ? <PrimaryLinks links={primaryLinks} pagePath={pagePath} /> : null}
      </div>
      <div className={css(style.itemsBasicContainer, style.itemsRightContainer)}>
        {cta ? <CtaButton {...cta} /> : null}
        {editions ? <Editions editions={editions} editionEndpoint={editionEndpoint} /> : null}
      </div>
    </div>
  );
};

const mapThemeProps = (theme: PrimaryHeaderThemeProps) => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor,
    surfaceStyle: theme.surfaceStyle,
  };
};

export const PrimaryHeader = (props: PrimaryHeaderDataProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <ErrorBoundary><PrimaryHeaderComponent {...themeContext} {...props} /></ErrorBoundary>;
};
